import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import featureNames from '@utils/featureToggle/constants';
import { RenderForAI } from '@components/common';
import { PAGE_TYPE, DEFAULT_METRIC, CURRENT_PRODUCT_MODE } from '@utils/constants';
import { getFilterFromRules, getRulesFromFilter, dataFilterMetricsList } from '@utils';
import { SUMMARY_METRICS } from '@store/summaryContainer/constants';
import { tid } from '@utils/test';
import {
  getMetricExcludeList,
  isMetricIncluded,
  genMetricList,
} from '@utils/metrics/metricHelpers';
import SampledDataWarning from '@components/common/SampledDataWarning';
import Latch from '@components/common/Latch';
import { MODE_ALERT_DIAGNOSTICS } from '@store/trends/constants';
import { defaultProps } from './utils/propTypes';
import { FilterDropdown } from '..';
import MemoDatePicker from './DatePickers';
import SummaryBreadcrumb from './SummaryBreadcrumb';
import DashboardTabs from '../../common/Dashboard/DashboardTabs';
import styles from './index.less';
import { calcComparisonInterval } from './utils/dates';
import DataSourceFilter from '../DataSourceFilter';
import AutoRefreshToggle from './AutoRefreshToggle';
import useSetExportBase from './hooks/useSetExportBase';
import { SUMMARY_PANEL_CONTROLS_ID } from './utils';
import { checkFeature } from '@src/utils/platform/features/checkFeature';
import { OptionRenderer } from './OptionsRenderer';
class SummaryContainer extends PureComponent {
  constructor(props) {
    super(props);
    this.summaryDatePickerRef = React.createRef();
    this.comparisonDatePickerRef = React.createRef();
    this.state = {};
    this.updateSummary = this.updateSummary.bind(this);
  }

  componentDidMount() {
    this.loadSummary();
  }

  componentDidUpdate(prevProps) {
    const { interval, filters, horizontalLayout }: any = this.props;
    if (
      interval !== prevProps.interval ||
      filters !== prevProps.filters ||
      horizontalLayout !== prevProps.horizontalLayout
    ) {
      this.updateSummary();
    }
  }

  loadSummary = () => {
    const { interval, setComparisonInterval, isSharing, features }: any = this.props;
    const durationAllowed = features[featureNames.DATA_ACCESS_DURATION];
    const { from, to } = calcComparisonInterval(interval, durationAllowed);

    // Align seconds of compare interval with current interval
    // Needed when compare series is shown in trends diagnostics chart
    const fromSeconds = moment(interval.from).seconds();
    const toSeconds = moment(interval.to).seconds();
    if (from) {
      from.seconds(fromSeconds);
    }
    if (to) {
      to.seconds(toSeconds);
    }

    // Don't set comparison interval automatically when it's a sharing page
    if (from && to && !isSharing) {
      setComparisonInterval(from.format(), to.format());
    }

    this.updateSummary();
  };

  updateSummary = () => {
    const { presetFid }: any = this.props;
    this.onChangeDataFilter(presetFid || 'all');
  };

  // filter metrics based on toggle
  changeMetrics = (presetFid) => {
    if (presetFid in dataFilterMetricsList) {
      return genMetricList(SUMMARY_METRICS[CURRENT_PRODUCT_MODE], dataFilterMetricsList[presetFid]);
    }
    return SUMMARY_METRICS[CURRENT_PRODUCT_MODE].slice(0);
  };

  // TODO: this feature is not supported on new trends
  // handle data source toggle actions
  onChangeDataFilter = (presetFid) => {
    const { setMetrics, selectedMetric, selectedMetricChart }: any = this.props;
    const metrics = this.changeMetrics(presetFid);
    setMetrics(metrics);

    // preset exclude list
    const excludeList = getMetricExcludeList(presetFid);

    // reset selected metric if it's part of exclude list
    if (isMetricIncluded(excludeList, selectedMetric)) {
      selectedMetricChart(DEFAULT_METRIC);
    }
  };

  applyFilterFromBreadcrumb = (key) => {
    const { drilldown, applyFilter: applyFilterAction, drillUp: drillUpAction }: any = this.props;

    const newFilter = [];
    drilldown.slice(1, key + 1).forEach((item) => {
      if (Array.isArray(item.rule[0])) {
        item.rule.forEach((r) => {
          newFilter.push(r);
        });
      } else {
        newFilter.push(item.rule);
      }
    });
    const allRules = getRulesFromFilter(newFilter, true);
    const filterToApply = getFilterFromRules(allRules, true);

    // identifier for preset filters
    const keyFilterObj = drilldown[key];
    if (keyFilterObj.presetFid) {
      filterToApply.presetFid = keyFilterObj.presetFid;
    }

    applyFilterAction(filterToApply);
    drillUpAction(key);
  };

  hasIntervalChanged = (newInterval) => {
    const { interval } = this.props;
    try {
      if (
        newInterval[0].isSame(moment(interval.from)) &&
        newInterval[1].isSame(moment(interval.to))
      ) {
        return false;
      }
    } catch (err) {
      // pass
      // falls back to current behaviour
    }
    return true;
  };

  validateUpdateInterval = (dates, short) => {
    const { updateInterval: updateIntervalAction }: any = this.props;

    // check if summary interval changed before triggering redux action
    // as there are multiple listeners
    if (this.hasIntervalChanged(dates)) {
      updateIntervalAction(dates[0].format(), dates[1].format(), short);
    }
  };

  render() {
    const {
      interval,
      comparison: { interval: comparisonInterval },
      setComparisonInterval,
      setComparisonUnavailable,
      features,
      sharingState,
      drilldown,
      id,
      isSharing,
      newTrendsLayout,
      dodStatus,
      mode,
      updateGranularity,
      horizontalLayout,
      liveModeSelected,
      savingState,
      pageKeyForSaveService,
      isSavedDashboard,
      savedDashboardCreatorId,
    }: any = this.props;

    // hide trend when dod is disabled for new layout
    const dod = features[featureNames.DAY_OVER_DAY] && (newTrendsLayout ? dodStatus : true);
    const share = features[featureNames.SHARE];

    let pageType = -1;
    if (isSharing) {
      pageType = PAGE_TYPE.SHARING;
    }
    const durationAllowed = features[featureNames.DATA_ACCESS_DURATION];

    return (
      <div className={styles.summaryContainer} {...tid('summary-panel')}>
        <div className="header-layout">
          <div className="left">
            <DashboardTabs pageType={pageType} id={id} />
          </div>
          {/**<div className="right">
            <LayoutToggle />
          </div>**/}
        </div>

        <div className={styles.topWrapper} {...tid('controls')} id={SUMMARY_PANEL_CONTROLS_ID}>
          <div style={{ flex: 1 }}>
            <FilterDropdown />
            <SummaryBreadcrumb
              drilldown={drilldown}
              applyFilterFromBreadcrumb={this.applyFilterFromBreadcrumb}
            />
          </div>
          <div className={styles.controls}>
            {mode !== MODE_ALERT_DIAGNOSTICS && (
              <RenderForAI>
                <DataSourceFilter />
              </RenderForAI>
            )}
            <SampledDataWarning />
            {liveModeSelected && <AutoRefreshToggle />}
            <div>
              <MemoDatePicker
                key={`${JSON.stringify(interval)}/${JSON.stringify(comparisonInterval)}`}
                interval={interval}
                comparisonInterval={comparisonInterval}
                setComparisonUnavailable={setComparisonUnavailable}
                setComparisonInterval={setComparisonInterval}
                dod={dod}
                summaryDatePickerRef={this.summaryDatePickerRef}
                validateUpdateInterval={this.validateUpdateInterval}
                comparisonDatePickerRef={this.comparisonDatePickerRef}
                durationAllowed={durationAllowed}
              />
            </div>
            {checkFeature('trends.base.options_popover', true) ? (
              <OptionRenderer
                horizontalLayout={horizontalLayout}
                sharingState={sharingState}
                isSavedDashboard={isSavedDashboard}
                savedDashboardCreatorId={savedDashboardCreatorId}
                pageKeyForSaveService={pageKeyForSaveService}
                id={id}
                interval={interval}
                savingState={savingState}
              />
            ) : (
              <></>
            )}
          </div>
        </div>
        <Latch useHook={useSetExportBase} params={{}} />
      </div>
    );
  }
}

SummaryContainer.defaultProps = defaultProps;

export default connect(null, {})(SummaryContainer);
